import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Boiler } from '../atoms/icons/boiler';
import { SwitchBox } from '../molecules/switch-box';
import { ValueBox } from '../molecules/value-box';
import { Group } from '../organisms/group';
import { FormattedValue } from '../store/formatted-value';
import { FormattedStatus } from '../store/formatted-status';
import { SolarPanel } from '../atoms/icons/solar-panel';
import { Battery } from '../atoms/icons/battery';
import { House } from '../atoms/icons/house';
import { HeatPump } from '../atoms/icons/heat-pump';
import { ArrowBottom } from '../atoms/icons/arrow-bottom';
import { ArrowTop } from '../atoms/icons/arrow-top';
import { ElectricalGrid } from '../atoms/icons/electrical-grid';
import { SpotSettingsUser } from '../organisms/settings/spot-settings-user';
import { HelpButton } from '../atoms/help-button';
import { useSelector } from '../store/utils';
import { get } from 'lodash';
import { Inverter } from '../../../core/src/shared/enums';
import { SpotWizardBanner } from '../templates/spot-wizard/spot-wizard-banner';
import { SpotOverview } from '../templates/spot/spot-overview';

const SpotPage = () => {
  const [batteryControl, overflowControl] = useSelector(
    (s) =>
      [
        get(s.variables, 'PV_BATTERY_CONTROL'),
        get(s.variables, 'PV_OVERFLOW_CONTROL'),
      ] as const
  );

  const [hes, hesLevel] = useSelector((s) => {
    const inverterConfig = s.variables.INVERTER_CONFIG;
    const inverterConfigArray = Object.values(inverterConfig ?? {});
    const inverterStatsArray = Object.values(s.variables.INVERTER_STATS ?? {});
    const hesLevel = inverterStatsArray[0]?.hesSpot?.level ?? '';

    return [inverterConfigArray[0]?.type === Inverter.Hes, hesLevel] as const;
  });

  return (
    <>
      <Group layout="list">
        <SwitchBox
          title={
            <FormattedMessage
              id="spot.status"
              defaultMessage="Spotové obchodování"
            />
          }
          switchVariableId="SP_ON"
          fullWidth
        />

        <SpotOverview />
      </Group>
      <Group
        title={
          <FormattedMessage
            id="spot.draft"
            defaultMessage="Režimy pro dnešek"
          />
        }
        help={{
          variant: 'tooltip',
          title: undefined,
          content: (
            <FormattedMessage
              id="spot.draft.info"
              defaultMessage="Režimy a doporučení pro dnešní den"
            />
          ),
        }}
        layout="scrollable"
      >
        <SwitchBox
          title={
            <FormattedMessage
              id="spot.draft.todaystatus_HW"
              defaultMessage="Akumulace bojleru"
            />
          }
          subtitle={<FormattedStatus type="spotHeatWater" />}
          visibility={{ variableId: 'HW_INSTALL' }}
          sx={{ mr: 2 }}
        />
        <SwitchBox
          title={
            <FormattedMessage
              id="spot.draft.todaystatus_HP"
              defaultMessage="Akumulace tepelka"
            />
          }
          subtitle={<FormattedStatus type="spotHeatPump" />}
          visibility={{ variableId: 'HP_ENABLED' }}
          sx={{ mr: 2 }}
        />
        <SwitchBox
          title={
            <FormattedMessage
              id="spot.draft.todaystatus_bat"
              defaultMessage="Akumulace baterie"
            />
          }
          subtitle={<FormattedStatus type="spotBattery" />}
          visibility={{ variableId: 'PV_BATTERY' }}
          sx={{ mr: 2 }}
        />
        <SwitchBox
          title={
            <FormattedMessage
              id="spot.draft.todaystatus_em"
              defaultMessage="Přetok"
            />
          }
          subtitle={<FormattedStatus type="spotSmartMeter" />}
          sx={{ mr: 2 }}
          visibility={{ variableId: 'PV_OVERFLOW_CONTROL' }}
        />
        {hes && (
          <SwitchBox
            title={
              <FormattedMessage
                id="spot.draft.hesLevel"
                defaultMessage="Úroveň řízení HES"
              />
            }
            subtitle={hesLevel}
            sx={{ mr: 2 }}
          />
        )}
      </Group>
      <Group layout="grid">
        <ValueBox
          title={
            <FormattedMessage
              id="spot.draft.lowprice"
              defaultMessage="Hranice zvýšeného odběru"
            />
          }
          value={
            <FormattedValue
              variableId="SP_LOW_PRICE"
              opts={{ style: 'currency' }}
            />
          }
          icon={<ArrowTop />}
          gridItem={{ size: { xs: 12, lg: 6 } }}
          type="row"
        />
        <ValueBox
          title={
            <FormattedMessage
              id="spot.draft.hiprice"
              defaultMessage="Hranice sníženého odběru"
            />
          }
          value={
            <FormattedValue
              variableId="SP_HI_PRICE"
              opts={{ style: 'currency' }}
            />
          }
          icon={<ArrowBottom />}
          gridItem={{ size: { xs: 12, lg: 6 } }}
          type="row"
        />

        <ValueBox
          title={
            <FormattedMessage
              id="spot.draft.actualpricelow"
              defaultMessage="Aktuální cena je nízká, zvyš spotřebu"
            />
          }
          visibility={{ variableId: 'SP_ACTUAL_PRICE_LOW' }}
          value={
            <FormattedValue
              variableId="SP_ACTUAL_PRICE"
              opts={{ style: 'currency' }}
            />
          }
          icon={<ArrowTop />}
          gridItem={{ size: { xs: 12, lg: 6 } }}
          type="row"
        />

        <ValueBox
          title={
            <FormattedMessage
              id="spot.draft.actualpricehi"
              defaultMessage="Aktuální cena je vysoká, sniž spotřebu"
            />
          }
          visibility={{ variableId: 'SP_ACTUAL_PRICE_HI' }}
          value={
            <FormattedValue
              variableId="SP_ACTUAL_PRICE"
              opts={{ style: 'currency' }}
            />
          }
          icon={<ArrowBottom />}
          gridItem={{ size: { xs: 12, lg: 6 } }}
          type="row"
        />
      </Group>
      <Group
        title={
          <FormattedMessage
            id="spot.prediction"
            defaultMessage="Odhady energií pro dnešek"
          />
        }
        layout="list"
      >
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="spot.prediction.pv"
              defaultMessage="Výroba elektrárny"
            />
          }
          visibility={{ variableId: 'PV_INSTALL' }}
          value={
            <FormattedValue
              variableId="SP_FORECAST_PV"
              opts={{
                style: 'unit',
                unit: 'kilowatt-hour',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          icon={<SolarPanel />}
        />
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="spot.prediction.home"
              defaultMessage="Běžná spotřeba objektu"
            />
          }
          value={
            <FormattedValue
              variableId="SP_FORECAST_HOME_CONSUMPTIN"
              opts={{
                style: 'unit',
                unit: 'kilowatt-hour',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          icon={<House />}
        />
        <ValueBox
          type="row"
          title={
            <>
              <FormattedMessage
                id="spot.prediction.sale"
                defaultMessage="Zbývající energie pro prodej přebytků"
              />
              <HelpButton
                variant="dialog"
                title={
                  <FormattedMessage
                    id="spot.prediction.sale"
                    defaultMessage="Zbývající energie pro prodej přebytků"
                  />
                }
                content={
                  <FormattedMessage
                    id="spot.prediction.sale.help"
                    defaultMessage="Zbývající plánovaná denní výše přebytků, která bude upřednostněna jako přetok do sítě před akumulací do baterie."
                  />
                }
              />
            </>
          }
          visibility={{
            variableIds: ['SP_OVERFLOW_OPTIMALIZATION', 'PV_OVERFLOW_CONTROL'],
          }}
          value={
            <FormattedValue
              variableId="SP_ENERGY_FOR_SALE"
              opts={{
                style: 'unit',
                unit: 'kilowatt-hour',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          icon={<ElectricalGrid />}
        />
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="spot.prediction.hp"
              defaultMessage="Tepelné čerpadlo"
            />
          }
          visibility={{ variableId: 'HP_ENABLED' }}
          value={
            <FormattedValue
              variableId="SP_FORECAST_HP_CONSUMPTIN"
              opts={{
                style: 'unit',
                unit: 'kilowatt-hour',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          icon={<HeatPump />}
        />
      </Group>
      <Group
        title={
          <FormattedMessage
            id="spot.flexibility"
            defaultMessage="Odhad flexibility pro dnešek"
          />
        }
        layout="list"
      >
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="spot.prediction.hw"
              defaultMessage="Dispoziční akumulace bojleru"
            />
          }
          value={
            <FormattedValue
              variableId="SP_FORECAST_HW_CONSUMPTIN"
              opts={{
                style: 'unit',
                unit: 'kilowatt-hour',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          visibility={{ variableId: 'HW_INSTALL' }}
          icon={<Boiler />}
        />
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="spot.prediction.battery"
              defaultMessage="Dispoziční akumulace baterie"
            />
          }
          visibility={{ variableId: 'PV_BATTERY' }}
          value={
            <FormattedValue
              variableId="SP_FORECAST_BATTERY_CONSUMPTIN"
              opts={{
                style: 'unit',
                unit: 'kilowatt-hour',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          icon={<Battery />}
        />
      </Group>

      <SpotWizardBanner />

      {batteryControl && overflowControl && (
        <Group
          title={
            <FormattedMessage
              id="spot.user.parameters"
              defaultMessage="Parametry spotového obchodování"
            />
          }
          layout="list"
          collapsible
        >
          <SpotSettingsUser />
        </Group>
      )}
    </>
  );
};

export default SpotPage;
